import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { ConfirmDialog } from "@tabipla/components";
import { useTripDetail } from "@/application/hooks/useTripDetail";
import { useTripDialogs } from "@/application/hooks/useTripDialogs";
import { useTripChat } from "@/application/hooks/useTripChat";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { LoadingState } from "../LoadingState";
import { ErrorState } from "../ErrorState";
import { TripCoverEditor } from "../TripCoverEditor";
import { TripDetailHeader } from "./TripDetailHeader";
import { TripDetailContent } from "./TripDetailContent";
import { TripDetailDialogs } from "./TripDetailDialogs";
import type { TripDetailContainerProps, TabType } from "./types";
import type { ViewMode } from "./ViewModeToggle";
import type { GenerateContext } from "../AiGenerateDialog";

export function TripDetailContainer({ tripId }: TripDetailContainerProps) {
  const { t } = useTranslation();

  // ==================== Hooks ====================
  const dialogs = useTripDialogs();
  const tripDetail = useTripDetail(tripId);
  const chat = useTripChat(tripId);

  // ==================== UI State ====================
  const [activeTab, setActiveTab] = useState<TabType>("itinerary");
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  // ==================== Effects ====================
  // Set default view mode based on trip mode
  const [hasSetInitialViewMode, setHasSetInitialViewMode] = useState(false);
  useEffect(() => {
    if (tripDetail.trip && !hasSetInitialViewMode) {
      if (tripDetail.tripMode === "traveling") {
        // During travel, timeline view is preferred for checking "what's next"
        setViewMode("timeline");
      }
      setHasSetInitialViewMode(true);
    }
  }, [tripDetail.trip, tripDetail.tripMode, hasSetInitialViewMode]);

  // ==================== Handlers ====================
  const handleGenerate = async () => {
    if (!tripDetail.trip?.startDate || !tripDetail.trip?.endDate) {
      toast.error(t("trips.generateRequiresDates"));
      return;
    }

    if (tripDetail.hasItinerary) {
      const confirmed = await dialogs.confirm({
        title: t("trips.generateItinerary"),
        description: t("trips.generateConfirm"),
        confirmText: t("common.generate"),
        cancelText: t("common.cancel"),
      });
      if (!confirmed) return;
    }

    dialogs.open.aiGenerate();
  };

  const handleAiGenerate = async (context: GenerateContext) => {
    try {
      await tripDetail.handleAiGenerate(context);
      dialogs.close("aiGenerate");
    } catch {
      // Error already handled in hook
    }
  };

  const handleSaveCover = async (emoji: string, color: string) => {
    await tripDetail.handleSaveCover(emoji, color);
    dialogs.close("coverEditor");
  };

  const handleInvite = async () => {
    await tripDetail.handleInvite(dialogs.open.invite);
  };

  // ==================== Loading/Error States ====================
  if (tripDetail.isLoading) {
    return (
      <DashboardLayout title={t("trips.detail")}>
        <LoadingState message={t("trips.loadingDetail")} />
      </DashboardLayout>
    );
  }

  if (tripDetail.error) {
    return (
      <DashboardLayout title={t("trips.detail")}>
        <ErrorState error={tripDetail.error} onRetry={tripDetail.refetch} />
      </DashboardLayout>
    );
  }

  if (!tripDetail.trip) {
    return (
      <DashboardLayout title={t("trips.detail")}>
        <ErrorState error={new Error(t("trips.notFound"))} />
      </DashboardLayout>
    );
  }

  // ==================== Render ====================
  return (
    <DashboardLayout
      title={tripDetail.trip.title}
      fabAction={{
        icon: MessageSquare,
        label: t("trips.chat"),
        onClick: () => chat.setShowChat(true),
      }}
    >
      <div className="animate-fade-in space-y-6">
        {/* Header */}
        <TripDetailHeader
          trip={tripDetail.trip}
          coverEmoji={tripDetail.coverEmoji}
          coverColor={tripDetail.coverColor}
          hasItinerary={tripDetail.hasItinerary}
          packingItems={tripDetail.packingItems}
          tripMode={tripDetail.tripMode}
          daysUntilStart={tripDetail.daysUntilStart}
          isGenerating={tripDetail.mutations.generate.isPending}
          isCreatingGroup={tripDetail.mutations.createGroupForTrip.isPending}
          onEditCover={() => dialogs.toggle("coverEditor")}
          onEditTrip={dialogs.open.editTrip}
          onInvite={handleInvite}
          onShare={dialogs.open.share}
          onGenerate={handleGenerate}
        />

        {/* Cover Editor Panel (positioned after header) */}
        {dialogs.dialogs.coverEditor && (
          <div className="border-b bg-background p-4">
            <TripCoverEditor
              destination={tripDetail.trip.destination}
              interests={tripDetail.trip.interests}
              currentEmoji={tripDetail.trip.coverEmoji}
              currentColor={tripDetail.trip.coverColor}
              onSave={handleSaveCover}
              disabled={tripDetail.mutations.updateTrip.isPending}
            />
          </div>
        )}

        {/* Content */}
        <TripDetailContent
          trip={tripDetail.trip}
          tripId={tripId}
          groupMembers={tripDetail.groupMembers}
          currentGroup={tripDetail.currentGroup}
          tripMode={tripDetail.tripMode}
          activeTab={activeTab}
          viewMode={viewMode}
          todaysItinerary={tripDetail.todaysItinerary}
          nextSpot={tripDetail.nextSpot}
          chat={chat}
          onTabChange={setActiveTab}
          onViewModeChange={setViewMode}
          onAddSpot={dialogs.setAddSpotItineraryId}
          onEditSpot={dialogs.setEditingSpot}
          onInviteClick={dialogs.open.invite}
          onGroupSelect={dialogs.open.groupSelect}
          onQuickExpense={dialogs.open.quickExpense}
        />
      </div>

      {/* Dialogs */}
      <TripDetailDialogs
        tripId={tripId}
        trip={tripDetail.trip}
        dialogs={dialogs}
        shareCode={tripDetail.mutations.share.data?.shareCode ?? tripDetail.trip.shareCode}
        onShare={tripDetail.handleShare}
        onUnshare={tripDetail.handleUnshare}
        isSharing={tripDetail.mutations.share.isPending || tripDetail.mutations.unshare.isPending}
        onAiGenerate={handleAiGenerate}
        isGenerating={tripDetail.mutations.generate.isPending}
        addSpotDestinationCoords={tripDetail.getAddSpotDestinationCoords(
          dialogs.spotDialogs.addSpotItineraryId
        )}
      />

      {/* Confirm Dialogs */}
      <ConfirmDialog {...dialogs.confirmDialogProps} />
      <ConfirmDialog {...chat.dialogProps} />
    </DashboardLayout>
  );
}
